import { useState } from 'react';
import { Copy, Check, Radio, FileText } from 'lucide-react';
import { buildRadioSitrep, buildCadNote, type Incident } from '@duty/shared';

/**
 * Deterministic SITREP / CAD-note generation. Always available (no AI). When AI
 * is enabled in a later epic, a "polish" action will rephrase this under the
 * grounding contract — it can never add facts beyond what's shown here.
 */
export function SitrepPanel({ incident }: { incident: Incident }) {
  const radio = buildRadioSitrep(incident);
  const cad = buildCadNote(incident);

  return (
    <div className="space-y-3">
      <OutputBlock icon={<Radio size={14} />} title="Radio-ready ITASC" text={radio} multiline />
      <OutputBlock icon={<FileText size={14} />} title="CAD-ready note" text={cad} />
      <p className="text-[11px] italic text-muted">
        Generated deterministically from the captured ITASC. Advisory only.
      </p>
    </div>
  );
}

function OutputBlock({
  icon,
  title,
  text,
  multiline = false,
}: {
  icon: React.ReactNode;
  title: string;
  text: string;
  multiline?: boolean;
}) {
  const [copied, setCopied] = useState(false);
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 1500);
    } catch {
      /* clipboard may be blocked; ignore */
    }
  };
  return (
    <div className="rounded-lg border border-border bg-surface-2/60">
      <div className="flex items-center justify-between border-b border-border px-3 py-1.5">
        <span className="inline-flex items-center gap-1.5 text-xs font-semibold text-muted">
          {icon}
          {title}
        </span>
        <button
          onClick={copy}
          className="inline-flex items-center gap-1 text-xs text-muted hover:text-accent"
          aria-label={`Copy ${title}`}
        >
          {copied ? <Check size={13} className="text-ok" /> : <Copy size={13} />}
          {copied ? 'Copied' : 'Copy'}
        </button>
      </div>
      <pre
        className={`px-3 py-2 text-sm text-text/90 ${
          multiline ? 'whitespace-pre-wrap' : 'whitespace-pre-wrap'
        } font-sans`}
      >
        {text}
      </pre>
    </div>
  );
}
